import cx from 'classnames';
import {
  CSSProperties,
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Spinner} from '~/common/components/Loader/Spinner';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {ECard} from '~/QuickEcards/components/ECard/ECard';
import {ECardActionsDrawer} from '~/QuickEcards/components/ECardActionsDrawer/ECardActionsDrawer';
import {OverlayAnimation} from '~/QuickEcards/components/OverlayAnimation/OverlayAnimation';
import {EcardContext} from '~/QuickEcards/context/EcardContext';
import {useCardAnimation} from '~/QuickEcards/hooks/useCardAnimation';
import {useCurrentRoute} from '~/QuickEcards/hooks/useCurrentRoute';
import {useAppSelector} from '~/QuickEcards/store';
import {
  selectBackground,
  selectSelectedOverlay,
  selectSelectedReveal,
} from '~/QuickEcards/store/reducers/ecard';
import {
  selectIsMessageEditorOn,
  selectMessage,
} from '~/QuickEcards/store/reducers/recipientMessages';
import {selectIsCreator} from '~/QuickEcards/store/selectors';
import {FlipBehavior} from '~/QuickEcards/types/ecard';

import './ViewEcard.sass';

interface ViewEcardProps {
  preview?: boolean;
  flipBehavior?: FlipBehavior;
  hideBackground?: boolean;
  noAnim?: boolean;
  openFullPreview?: () => void;
  messageOverride?: string;
  ready?: boolean;
  hideReactionDrawer?: boolean;
  showTemplateMessage?: boolean;
}

export const ViewEcard: FC<ViewEcardProps> = ({
  preview = false,
  flipBehavior = 'auto',
  hideBackground = false,
  noAnim = false,
  openFullPreview,
  messageOverride,
  ready = true,
  hideReactionDrawer = false,
  showTemplateMessage = preview,
}) => {
  const {effectVideo, revealVideo} = useContext(EcardContext);
  const {currentRoute} = useCurrentRoute();
  const isDesktop = useIsLargishAndUp();
  const background = useAppSelector((state) => selectBackground(state, isDesktop));
  const selectedMessage = useAppSelector(selectMessage);
  const isCreator = useAppSelector(selectIsCreator);
  const isMessageEditorOn = useAppSelector(selectIsMessageEditorOn);
  const message = messageOverride ?? selectedMessage;
  const prevMessageRef = useRef(message);

  /* Get updated revealId and effectId from endpoint */
  const revealId = useAppSelector(selectSelectedReveal);
  const effectId = useAppSelector(selectSelectedOverlay);

  /* Values checking if reveal/effect has been initialized */
  const hasReveal = revealId !== null;
  const hasEffect = effectId !== null;

  const [animation, setAnimation] = useState<string>('');
  const [showCard, setShowCard] = useState<boolean>(false);
  const [animationFinished, setAnimationFinished] = useState<boolean>(false);

  const {revealFinishedRef, effectFinishedRef, playFullAnimation, replayAnimation} =
    useCardAnimation({
      hasReveal: !!revealId, // Whether or not card has a reveal
      hasEffect: !!effectId, // Whether or not card has an effect
      message,
      revealVideo,
      effectVideo,
      setAnimation,
      setAnimationFinished,
      disableFlip: flipBehavior === 'disable',
    });

  useEffect(() => {
    if (!ready) return; // Wait until all necessary data for animation is loaded
    if (
      flipBehavior !== 'disable' &&
      animationFinished &&
      (message || isMessageEditorOn || flipBehavior === 'force')
    ) {
      setAnimation('flip');
    }
    prevMessageRef.current = message;
  }, [ready, animationFinished, message, flipBehavior, isMessageEditorOn]);

  /* Once all included animations are loaded, play the full animation */
  useEffect(() => {
    if (!ready) return undefined; // Wait until all necessary data for animation is loaded

    let timeoutId: NodeJS.Timeout;
    if (hasReveal && hasEffect) {
      // This is needed for when there is no reveal or effect - we want to terminate the reveal/effect refs for the animations
      // to start
      if (revealId === null || revealId === '') {
        revealFinishedRef.current = true;
      }
      if (effectId === null || effectId === '') {
        effectFinishedRef.current = true;
      }

      timeoutId = setTimeout(async () => {
        setShowCard(true);
        if (noAnim) {
          setAnimation('slide-up');
          setAnimationFinished(true);
        } else {
          await playFullAnimation();
        }
      }, 50);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    ready,
    revealId,
    effectId,
    hasReveal,
    hasEffect,
    playFullAnimation,
    revealFinishedRef,
    effectFinishedRef,
    noAnim,
  ]);

  const revealAnimation = useMemo(
    (): ReactElement => (
      <OverlayAnimation
        revealVideo={revealVideo}
        isReveal
        portalToBody={!preview}
        onEnded={() => {
          revealFinishedRef.current = true;
        }}
      />
    ),
    [revealVideo, preview, revealFinishedRef]
  );

  const effectAnimation = useMemo(
    (): ReactElement => (
      <OverlayAnimation
        effectVideo={effectVideo}
        portalToBody={!preview}
        onEnded={() => {
          effectFinishedRef.current = true;
        }}
        hideOnLoad
      />
    ),
    [effectVideo, preview, effectFinishedRef]
  );

  const handleCardToggle = useCallback(() => {
    setAnimation((anim) => (anim === 'flip' ? 'unflip' : 'flip'));
  }, []);

  useEffect(() => {
    if (isMessageEditorOn) return;
    setAnimation((anim) => {
      if (anim && !message) return 'unflip';
      return anim;
    });
  }, [isMessageEditorOn, message]);

  const style: CSSProperties | undefined = hideBackground || !background ? undefined : {background};

  return (
    <>
      <div
        className={cx('view-ecard__container', currentRoute?.path, {
          preview,
          'view-ecard__hide-reaction-drawer': hideReactionDrawer,
        })}
      >
        {showCard ? (
          <>
            {hideBackground ? null : <div className="view-ecard__background" style={style} />}
            <ECard
              message={showTemplateMessage && !message ? 'Your note goes here' : message}
              animation={animation}
              setAnimation={setAnimation}
              animationFinished={animationFinished}
              preview={preview}
              replayAnimation={noAnim ? undefined : replayAnimation}
              openFullPreview={noAnim ? openFullPreview : undefined}
              handleCardToggle={handleCardToggle}
              disableFlip={flipBehavior === 'disable'}
            />
            {hideReactionDrawer ? null : (
              <ECardActionsDrawer show={animationFinished} isCreator={isCreator} />
            )}
          </>
        ) : (
          <div className="view-ecard__loading-overlay">
            <Spinner />
          </div>
        )}
      </div>
      {hasReveal && !noAnim && revealAnimation}
      {hasEffect && !noAnim && effectAnimation}
    </>
  );
};
